<template>
  <div :class="{'page-top-nav-container': true, 'page-top-nav-proxy': appUserProxyAccess, 'page-top-nav-local': appIsLocalSite, 'page-top-nav-staging': appIsStagingSite }">
    <div class="page-top-nav-pill">
      <div class="synergy-logo-container">
        <router-link to="/">
          <img class="synergy-logo" src="/glass/img/layout/navbar-logo.png" />
        </router-link>
      </div>

      <NavbarSystemSearch v-if="isAccountActivated" />

      <div class="glass-nav glass-navbar-top-links ml-2 glass-navbar-mainmenu" v-if="isAccountActivated">
        <NavbarMenuItem
          v-for="menuItem in menu"
          :key="menuItem.id"
          :menuItem="menuItem">
        </NavbarMenuItem>
      </div>

      <ul class="glass-nav glass-navbar-top-links navbar-right">
        <li class="dev-labels">
          <span class="label label-navbar-proxy mr-1" v-if="appUserProxyAccess"><i class="fas fa-wifi icon-anim-beat"></i> PROXY</span>
          <span class="label label-navbar-localdev mr-1" v-if="appIsLocalSite"><i class="fas fa-exclamation-circle"></i> LOCAL</span>
          <span class="label label-navbar-staging" v-if="appIsStagingSite"><i class="fas fa-exclamation-circle"></i> STAGING</span>
        </li>
        <li v-if="isSynergyAIAvailable">
          <div class="glass-navbar-option synergy-navbar-ai">
            <a href="#" aria-expanded="false" @click.prevent="displaySynergyAI">
              <i class="fas fa-stars"></i> Synergy AI
            </a>            
          </div>
        </li>
        <li v-if="isAccountActivated">
          <div class="glass-navbar-option glass-navbar-new-resource-menu">
            <a data-toggle="dropdown" href="#" aria-expanded="false">
              <i class="fas fa-plus"></i>
            </a>
            <ul class="dropdown-menu">
              <DropdownMenuLabel>Quick Create</DropdownMenuLabel>
              <DropdownMenuDivider />
              <DropdownMenuItem icon="fa-shopping-cart" v-if="userHasCreatePermission(PermissionTypes.OrderPermissionTypes.order)" link="/orders/new">New Order</DropdownMenuItem>
              <DropdownMenuItem icon="fa-user-circle" v-if="userHasCreatePermission(PermissionTypes.ContactPermissionTypes.contact)" link="/contacts/new">New Contact</DropdownMenuItem>
              <DropdownMenuItem icon="fa-dolly-flatbed" v-if="userHasCreatePermission(PermissionTypes.PurchasingPermissionTypes.purchaseOrder)" link="/purchasing/purchaseorders/new">New Purchase Order</DropdownMenuItem>
            </ul>
          </div>
        </li>
        <li v-if="isAccountActivated">
          <div class="glass-navbar-option glass-navbar-help-menu">
            <a data-toggle="dropdown" href="#" aria-expanded="false">
              <i class="fas fa-question"></i>
              <span class="unread-notification" v-if="isChangeLogUpdated"></span>
            </a>
            <ul class="dropdown-menu">
              <DropdownMenuLabel>Support</DropdownMenuLabel>
              <DropdownMenuDivider />
              <DropdownMenuItem icon="fa-phone-alt" link="/support/ticket" v-if="userHasCreatePermission(PermissionTypes.AdminPermissionTypes.helpdeskSupport)">Create Support Ticket</DropdownMenuItem>
              <DropdownMenuDivider v-if="userHasCreatePermission(PermissionTypes.AdminPermissionTypes.helpdeskSupport)" />
              <DropdownMenuItem icon="fa-question-circle" link="/support">Support Center</DropdownMenuItem>
              <DropdownMenuDivider />
              <DropdownMenuItem icon="fa-newspaper" link="/support/whatsnew" :badge="isChangeLogUpdated ? 'NEW' : ''">What&apos;s New</DropdownMenuItem>
            </ul>
          </div>
        </li>
        <li>
          <div class="glass-navbar-option">
            <a class="glass-navbar-top-notification" @click.prevent="displayNotifications">
              <i :class="['fal', 'fa-bell', 'glass-notification-icon', notificationUnreadCount > 0 ? 'active' : '', shakeNotificationBell ? 'animate' : '']"></i>
              <span class="glass-notification-badge text-center" v-if="notificationUnreadCount > 0">{{notificationUnreadCount}}</span>
            </a>
          </div>
        </li>
        <li>
          <div>
            <a data-toggle="dropdown" href="#" aria-expanded="false">
              <div class="glass-navbar-avatar-container">
                <Avatar :image="profileAvatarImage" :name="profileName" :editable="false" :size="42" />
                <div class="glass-navbar-avatar-warning" v-if="isSubscriptionExpired || displaySubscriptionExpiryWarning">
                  <i class="fas fa-exclamation" />
                </div>
              </div>
            </a>
            <ul class="dropdown-menu">
              <DropdownMenuLabel>{{profileName}}</DropdownMenuLabel>
              <DropdownMenuDivider />
              <template v-if="isSubscriptionExpired || displaySubscriptionExpiryWarning">
                <DropdownMenuItem icon="fa-exclamation-triangle" @click="displayAccount" v-if="isSubscriptionExpired">Subscription Expired</DropdownMenuItem>
                <DropdownMenuItem icon="fa-exclamation-triangle" @click="displayAccount" v-if="displaySubscriptionExpiryWarning">Subscription expiring soon</DropdownMenuItem>
                <DropdownMenuDivider />
              </template>
              <DropdownMenuItem icon="fa-user-circle" @click="viewUserProfile" v-if="isAccountActivated">View Profile</DropdownMenuItem>
              <DropdownMenuDivider v-if="canUpdateTheme" />
              <DropdownMenuItem icon="fa-moon" @click="setLocalTheme('theme-dark')" v-if="canUpdateTheme" :disabled="isThemeDark">Dark Theme (Experimental)</DropdownMenuItem>
              <DropdownMenuItem icon="fa-sun" @click="setLocalTheme('theme-light')" v-if="canUpdateTheme" :disabled="isThemeLight">Light Theme</DropdownMenuItem>
              <DropdownMenuDivider />
              <DropdownMenuItem icon="fa-sign-out" @click="logout">Logout</DropdownMenuItem>
            </ul>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>


<style scoped lang="scss">
$page-top-nav-height: 60px;

.page-top-nav-container {
  width: 100%;
  height: $page-top-nav-height;
  padding: 6px 10px 0px 10px;
}
.app-editormode .page-top-nav-container {
  padding-top: 3px;
}
.page-top-nav-container.page-top-nav-proxy .page-top-nav-pill {
  background-image: linear-gradient(135deg, #0f3118 33.33%, #0d3a19 33.33%, #0d3a19 50%, #0f3118 50%, #0f3118 83.33%, #0d3a19 83.33%, #0d3a19 100%);
  background-size: 42.43px 42.43px;
}
.page-top-nav-container.page-top-nav-staging .page-top-nav-pill {
  background-image: linear-gradient(135deg, #0f0f0f 33.33%, #303030 33.33%, #303030 50%, #0f0f0f 50%, #0f0f0f 83.33%, #303030 83.33%, #303030 100%);
  background-size: 42.43px 42.43px;
}
.page-top-nav-container.page-top-nav-local .page-top-nav-pill {
  background-image: linear-gradient(135deg, #0f0f0f 33.33%, #303030 33.33%, #303030 50%, #0f0f0f 50%, #0f0f0f 83.33%, #303030 83.33%, #303030 100%);
  background-size: 42.43px 42.43px;
}

.page-top-nav-pill {
  border-radius: $layout-border-radius;
  background-color: #101724;
  width: 100%;
  height: $layout-topnav-pill-height;
  display: flex;
  flex-direction: row;
}
.synergy-logo-container {
  display: inline-block;
  margin-top: 11px;
  margin-left: 6px;
  margin-right: 10px;
}
.synergy-logo {
  position: relative;
  height: 32px;
  transition: transform .15s ease-in;
}
.synergy-logo-container a:hover .synergy-logo {
  transform: rotate(90deg);
}

.navbar-right {
  padding-right: 10px;
  min-width: 250px;
  align-items: right;
  justify-content: right;
  margin-left: auto;
  overflow: hidden;
}

.label-navbar-proxy {
  background-color: #83e09c;
  color: #000000;
}
.label-navbar-localdev {
  background-color: #79c9cc;
  color: #000000;
}
.label-navbar-staging {
  background-color: #e6d4aa;
  color: #000000;
}
.dev-labels .label {
  font-size: 13px;
}

</style>


<script>
import { mapState } from "vuex"

import themeMixin from '@/mixins/theme'
import Avatar from '@/components/avatar'
import NavbarSystemSearch from '@/components/page/navbarSystemSearch.vue'
import NavbarMenuItem from '@/components/page/navbarMenuItem'
import UserChangePasswordDialog from '@/modules/users/views/userChangePasswordDialog.vue'
import EditLogoImageDialog from '@/modules/core/views/editLogoImageDialog.vue'

export default {
  name: 'TopNavBar',
  mixins: [themeMixin],
  components: {
    Avatar,
    NavbarSystemSearch,
    NavbarMenuItem
  },
  computed: {
    ...mapState({
      profileName: state => state.profile.profileUser.name,
      menu: state => state.profile.menuList,
      profileLogoURL: state => state.profile.profileUser.logoURL,
      notificationUnreadCount: state => state.profile.notifications.unreadCount,
      isChangeLogUpdated: state => state.profile.changeLogUpdatedAlert,
      pageErrorInfo: state => state.page.pageError,
      isAccountInitialBillingSetup: state => state.account.isAccountInitialBillingSetup,
      isAccountActivated: state => state.account.isAccountActivated,
      isSubscriptionExpired: state => state.account.isSubscriptionExpired,
      isSubscriptionExpiringSoon: state => state.account.isSubscriptionExpiringSoon,
      appUserProxyAccess: state => state.system.appUserProxyAccess
    }),
    displaySubscriptionExpiryWarning() {
      // Only display 'expiry soon' warnings for subscriptions if the user has ability to view account info
      return !this.isSubscriptionExpired && this.isSubscriptionExpiringSoon && this.userHasReadPermission(this.PermissionTypes.AdminPermissionTypes.account)
    },
    profileAvatarImage() {
      if (this.profileLogoURL && this.profileLogoURL != '')
        return this.profileLogoURL
      return '/glass/img/user-overview-male.png'
    },
    appIsStagingSite() {
      return !this.$glass.isLiveSite && !this.$glass.isLocalSite
    },
    appIsLocalSite() {
      return this.$glass.isLocalSite
    },
    canUpdateTheme() {
      // Only available on local testing for now
      return this.appIsLocalSite
    },
    isSynergyAIAvailable() {
      return false
      return this.isAccountActivated && this.appIsLocalSite
    }
  },
  data() {
    return {
      shakeNotificationBell: false
    }
  },
  watch: {
    notificationUnreadCount: function(newVal, oldVal) {
      if(this.notificationUnreadCount > 0 && (newVal > oldVal || oldVal === undefined)) {
        this.shakeNotificationBell = true

        // Remove after a few seconds
        setTimeout(() => { this.shakeNotificationBell = false }, 5000)
      }
    }
  },
  mounted() {
    this.loadLocalSettings()
  },
  methods: {
    loadLocalSettings() {
      let storedTheme = localStorage.getItem('profile-theme-class')
      if(storedTheme)
        this.setThemeClass(storedTheme)
    },
    logout(event) {
      this.$authService.clearAuthSession();
      this.$store.commit("profile/clearProfile")
      this.$store.commit("account/clearAccountData")
      this.$router.push("/login");
    },

    requestProfileData() {
      // Refresh the profile
      this.$api.get("/profile").then(response => {
        this.$store.commit("profile/setProfile", response.data);
      })
      this.$apiService.adminService.getAccount().then(response => {
        this.$store.commit("account/setAccountData", response.data)
      })
    },

    profileChangePassword() {
      this.displayModal(UserChangePasswordDialog)
      .then((success) => {
        if(success) {
          this.successToast('Password Updated')
          this.requestData()
        }
      })
    },

    profileUpdateImage() {
      this.displayModal(EditLogoImageDialog, { 
        resourceId: 'current',
        APIresource: 'users',
        currentURL: this.profileLogoURL,
        circleCrop: true
      })
      .then((success) => {
        if(success) {
          this.successToast('Logo Updated')
          this.requestProfileData()
        }
      })
    },

    profileRemoveImage() {
      this.confirmDeleteMessage('Remove Profile Image?', 'Are you sure you remove your profile image?')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteLogoImage('current', 'users').then(response => {
            this.successToast('Image Removed')
            this.requestProfileData()
          })
        }
      })
    },

    displayNotifications(evt) {
      this.$router.push("/communication/notifications");
    },
    displayAddNewPopover(evt) {
      this.$refs.addNewPopover.displayFromElement(evt.target)
    },
    viewWhatsNew() {
      this.$router.push("/help/whatsnew");
    },

    reloadPage() {
      this.$router.go(0)
    },

    viewUserProfile() {
      this.$router.push('/users/profile')
    },
    setLocalTheme(themeClass) {
      localStorage.setItem('profile-theme-class', themeClass)
      this.setThemeClass(themeClass)
    },

    displayAccount() {
      if(this.userHasReadPermission(this.PermissionTypes.AdminPermissionTypes.account)) {
        this.$router.push('/admin/account')
      }
    },

    displaySynergyAI() {
      this.$emit('displaySynergyAI')
    }
  }
}
</script>